/**
 * TopicPicker
 * -----------
 * The chapter's topics in teaching order, for a teacher to jump the class to
 * any of them without leaving the player. Used in the bottom bar's popover and
 * on the topic-complete screen.
 */

import { Check, Play } from 'lucide-react';

export interface TopicPickerItem {
  id: string;
  name: string;
  /** The topic the class is on now. */
  current: boolean;
  /** Already taught in this session. */
  done: boolean;
}

interface TopicPickerProps {
  topics: TopicPickerItem[];
  onPick: (topic: TopicPickerItem) => void;
  /** A launch is in flight; picking again would send two. */
  disabled?: boolean;
}

export const TopicPicker = ({ topics, onPick, disabled = false }: TopicPickerProps) => (
  <ol className="flex max-h-72 w-72 flex-col gap-1 overflow-y-auto text-left">
    {topics.map((t, i) => (
      <li key={t.id}>
        <button
          type="button"
          disabled={disabled || t.current}
          onClick={() => onPick(t)}
          className={`flex w-full items-center gap-2 rounded-lg px-2.5 py-2 text-xs transition disabled:cursor-default ${
            t.current
              ? 'bg-emerald-400/20 text-emerald-50'
              : 'text-white/80 hover:bg-white/10 disabled:opacity-50'
          }`}
        >
          <span className="w-5 shrink-0 tabular-nums text-white/45">{i + 1}.</span>
          <span className="flex-1 truncate">{t.name}</span>
          {t.current ? (
            <span className="inline-flex items-center gap-1 text-[10px] font-semibold uppercase text-emerald-200">
              <Play className="h-3 w-3" /> Now
            </span>
          ) : t.done ? (
            <span className="inline-flex items-center gap-1 text-[10px] font-semibold uppercase text-white/50">
              <Check className="h-3 w-3" /> Done
            </span>
          ) : null}
        </button>
      </li>
    ))}
  </ol>
);
